import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren, HostListener } from '@angular/core';
import { CmsService, CMSTabParagraphContainer, Product, ProductScope, WindowRef } from '@spartacus/core';
import { combineLatest, Observable, of, catchError } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take, tap } from 'rxjs/operators';
import { CmsComponentData, ComponentWrapperDirective, CurrentProductService } from "@spartacus/storefront";
import { GenProductService } from '../../services/product.service';
import { GenApplicableProductsResponse } from '../../models/product.model';
import { ActivatedRoute } from '@angular/router';
import { GenDocumentService } from 'src/app/generac/services/document.service';

@Component({
  selector: 'gen-tab-paragraph-container',
  templateUrl: './tab-paragraph-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GenTabParagraphContainerComponent implements AfterViewInit, OnInit {
  activeTabNum = 0;
  ariaLabel: string;

  @ViewChildren(ComponentWrapperDirective)
  children!: QueryList<ComponentWrapperDirective>;

  tabTitleParams: (Observable<any> | null)[] = [];

  showApplicableProducts: boolean = false;
  isCurrentProductWarranty: boolean;
  applicableProductsInfoMsg: string = '';
  applicableProductsTooltip: string = '';
  isMobileView: boolean;

  constructor(
    public componentData: CmsComponentData<CMSTabParagraphContainer>,
    protected cmsService: CmsService,
    protected winRef: WindowRef,
    protected currentProductService: CurrentProductService,
    protected productService: GenProductService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    public documentService: GenDocumentService,
  ) { }

  @HostListener('window:resize')
  onResize() {
    this.isMobileView = this.winRef?.nativeWindow.innerWidth <= 767;
  }

  components$: Observable<any[]> = this.currentProductService
    .getProduct([ProductScope.DETAILS, ProductScope.ATTRIBUTES])
    .pipe(
      distinctUntilChanged(),
      switchMap((product: Product) => {
        this.activeTabNum = 0;
        this.isCurrentProductWarranty = product.categories?.some(
          (category: any) => category.code === 'extended-warranties'
        );

        return combineLatest([
          this.productService.getApplicableProducts(product?.code, 0).pipe(take(1)),
          this.documentService.getDocuments('model', product?.code, 0, 1).pipe(
            take(1),
            catchError(() => of({ documents: [] }))
          ),
          of(product),
        ]);
      }),
      switchMap(([applicableProducts, documentResponse, product]) => {
        this.showApplicableProducts = applicableProducts?.results.length > 0;
        const hasDocuments = documentResponse?.documents?.length > 0;

        return this.componentData.data$.pipe(
          distinctUntilChanged((x, y) => x?.components === y?.components),
          take(1),
          tap((data: CMSTabParagraphContainer) => {
            this.ariaLabel = `${data?.uid}.tabPanelContainerRegion`;
          }),
          switchMap((data) =>
            combineLatest(
              (data?.components ?? '').split(' ').map((component) =>
                this.cmsService.getComponentData<any>(component).pipe(
                  distinctUntilChanged(),
                  take(1),
                  map((tab) => {
                    if (!tab) return undefined;

                    if ((tab.uid === 'WarrantyApplicableProductsComponent' || tab.uid === 'ApplicableProductsComponent') && !this.showApplicableProducts) {
                      return undefined;
                    }

                    if (tab.uid === 'ManualsListComponent' && !hasDocuments) {
                      return undefined;
                    }

                    if (tab.uid === 'ProductDetailsTabComponent' && product.servicePart) {
                      return undefined;
                    }

                    if (tab.uid === 'ProductNotesTabComponent' && (!product.servicePart || !product?.materialNotes || product?.materialNotes?.length < 1)) {
                      return undefined;
                    }

                    if (!tab?.flexType) {
                      tab = {
                        ...tab,
                        flexType: tab.typeCode,
                      };
                    }

                    const title =
                      tab.uid === 'ApplicableProductsComponent' && this.isCurrentProductWarranty
                        ? 'Warranty' + tab.uid
                        : tab.uid;

                    if (tab.uid === 'ApplicableProductsComponent') {
                      this.applicableProductsInfoMsg = this.isCurrentProductWarranty
                        ? tab.warrantyTooltipMessage
                        : tab.tooltipMessage;
                    }

                    return {
                      ...tab,
                      title: `${data.uid}.tabs.${title}`,
                    };
                  })
                )
              )
            )
          ),
          switchMap((components) =>
            of(components?.filter((component) => component?.title))
          )
        );
      })
    );

  select(tabNum: number, event?: MouseEvent): void {
    this.activeTabNum = this.activeTabNum === tabNum ? -1 : tabNum;
    if (event && event?.target) {
      const target = event.target as HTMLElement;
      const parentNode = target.parentNode as HTMLElement;
      this.winRef?.nativeWindow?.scrollTo({
        left: 0,
        top: parentNode.offsetTop,
        behavior: 'smooth',
      });
    }
  }

  ngOnInit(): void {
    this.onResize();
    this.activeTabNum = this.winRef?.nativeWindow?.history?.state?.activeTab ?? this.activeTabNum;
  }

  ngAfterViewInit(): void {
    // If the sub cms components data exist, the components created before ngAfterViewInit are called.
    // In this case, the title parameters are directly pulled from them.
    if (this.children.length > 0) {
      this.getTitleParams(this.children);
    }

    this.route.fragment.subscribe(fragment => {
      const tabUidMap: { [key: string]: string } = {
        'DocumentsTab': 'ManualsListComponent'
      };

      const targetUid = tabUidMap[fragment ?? ''];
      if (targetUid) {
        setTimeout(() => {
          this.components$.pipe(take(1)).subscribe(components => {
            const tabIndex = components.findIndex(comp => comp?.uid === targetUid);

            if (tabIndex !== -1) {
              this.activeTabNum = tabIndex;
              this.cdr.markForCheck();
            }
          });
        }, 300);
      }
    });
  }

  tabCompLoaded(componentRef: any): void {
    this.tabTitleParams.push(componentRef.instance.tabTitleParam$);
  }

  protected getTitleParams(children: QueryList<ComponentWrapperDirective>) {
    children.forEach((comp) => {
      this.tabTitleParams.push(comp['cmpRef']?.instance.tabTitleParam$ ?? null);
    });
  }
}
